import { Injectable, inject, signal, computed } from '@angular/core';
import { ApiService } from '../core/api.service';
import { environment } from '../../environments/environment';
import { AccountDataDetail, Tariff } from './account.model';

export type AuthMethodName = 'email' | 'google' | 'mailru' | 'telegram' | 'yandex' | 'vk'
export interface AuthMethod {
  name: AuthMethodName;
  username: string;
}
@Injectable({
  providedIn: 'root'
})
export class AccountService {

  private api = inject(ApiService)
  private urlPath = `${environment.api.url}/ui/private/account`

  private authMethods: AuthMethodName[] = ['email', 'google', 'mailru', 'telegram', 'yandex', 'vk'];

  /**
   * @deprecated Использовать UserService.getProfile() вместо этого метода
   */
  accountDetail = signal<AccountDataDetail | null>(null);

  /**
   * @deprecated Use getLastTariff from user.selectors.ts instead
   */
  tariff = signal<Tariff | null>(null);

  /**
   * @deprecated Use getProfileName from user.selectors.ts instead
   */
  accountName = computed(() => {
    const acc = this.accountDetail();
    return acc?.email ?? acc?.telegram?.username ?? acc?.telegramId ?? '';
  });

  /**
   * @deprecated Нужно реализовать в user.selectors.ts
   */
  linkedMethods = computed(() => {
    const acc = this.accountDetail();
    if (!acc) return [];

    return this.authMethods
      .filter(name => {
        const val = acc[name as keyof typeof acc];
        if (name === 'email') return !!val;
        return val != null && typeof val === 'object';
      })
      .map(name => {
        const val = acc[name as keyof typeof acc];
        if (name === 'email') return { name, username: val as string };
        const obj = val as unknown;
        if (typeof obj == 'object' && obj != null) {
          const email = 'email' in obj ? String((obj as { email?: string | null }).email ?? '') : '';
          const username = 'username' in obj ? String((obj as { username?: string | null }).username ?? '') : '';
          return { name, username: email || username || '' };
        }
        return { name, username: '' };
      });
  });

  /**
   * @deprecated Нужно реализовать в user.selectors.ts
   */
  availableMethods = computed(() => {
    const acc = this.accountDetail();
    if (!acc) return [];

    return this.authMethods
      .filter(name => {
        const val = acc[name as keyof typeof acc];
        if (name === 'email') return val === null;
        return val === undefined;
      })
      .map(name => ({ name, username: null }));
  });

  /**
   * @deprecated Нужно реализовать в user.selectors.ts
   */
  canUnlinkServices = computed(() => {
    const acc = this.accountDetail();
    if (!acc) return false;

    // список всех привязанных сервисов, исключая email
    const linked = this.linkedMethods().filter(m => m.name !== 'email');

    // если есть пароль — можно отвязать всегда
    if (acc.hasPassword) return true;

    // без пароля можно только если сервисов > 1
    return linked.length > 1;
  });


  getAccountDetail() {
    return this.api.get<AccountDataDetail>(`${this.urlPath}/detail`)
  }

  addIdp(idpName: string, idpData: unknown) {
    return this.api.post(`${this.urlPath}/idp/add-idp`, {
      idp_name: idpName,
      idp_data: idpData
    });
  }

  deleteIdp(idpName: string) {
    return this.api.post(`${this.urlPath}/idp/delete-idp`, {
      idp_name: idpName
    });
  }

  deleteAccount() {
    return this.api.get(`${this.urlPath}/delete`)
  }

  setPassword(newPassword: string) {
    return this.api.post(`${this.urlPath}/password/set`, {
      'new_password': newPassword
    })
  }

  deletePassword(oldPassword: string) {
    return this.api.post(`${this.urlPath}/password/unset`, {
      'old_password': oldPassword
    })
  }

  changePassword(newPassword: string, oldPassword: string) {
    return this.api.post(`${this.urlPath}/password/change`, {
      'new_password': newPassword,
      'old_password': oldPassword,
    })
  }

  changeEmail(email: string, password?: string) {
    const body: { email: string; password?: string } = { email };
    if (password) body.password = password;

    return this.api.post(`${this.urlPath}/email/set`, body);
  }

  /**
   * @deprecated Использовать UserService.getProfile() вместо этого метода
   */
  loadAccountDetail() {
    return this.getAccountDetail().subscribe(data => {
      this.accountDetail.set(data);

      const tariffs = data?.tariffs || [];
      const latest = tariffs.length
        ? tariffs.reduce((latest, t) => !latest || (t.endTime && latest.endTime && t.endTime > latest.endTime) ? t : latest, null as Tariff | null)
        : null;

      this.tariff.set(latest);
    });
  }

}
